import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { ArrowBack as BackIcon, Send as SendIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import SEOHead from '../components/SEOHead';

const ForumNewPost = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [categories, setCategories] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchCategories = async () => {
      try {
        const response = await api.get('/forum/categories');
        setCategories(response.data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, [token, navigate]);

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-');
      if (!tags.includes(newTag) && tags.length < 5) {
        setTags([...tags, newTag]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !content.trim() || !categoryId) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/forum/posts', {
        title: title.trim(),
        content: content.trim(),
        category_id: categoryId,
        tags,
        is_anonymous: isAnonymous,
      });

      navigate(`/forum/${response.data.slug}`);
    } catch (err) {
      // Show detailed validation errors if available
      if (err.response?.data?.details?.length > 0) {
        setError(err.response.data.details.join('. '));
      } else if (err.response?.data?.error?.includes('Security')) {
        setError('Security verification failed. Please refresh and try again.');
      } else {
        setError(err.response?.data?.error || 'Failed to create post. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Group categories by region
  const categoriesByRegion = categories.reduce((acc, cat) => {
    if (!acc[cat.region]) acc[cat.region] = [];
    acc[cat.region].push(cat);
    return acc;
  }, {});

  return (
    <>
      <SEOHead
        title="Start a Discussion | Travel Community - Déjà Vu Nomad"
        description="Share your travel experience, ask questions, or start a discussion with our global travel community."
        canonical="https://dejavunomad.com/forum/new"
      />

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/forum')}
          sx={{ mb: 3 }}
        >
          Back to Community
        </Button>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              ✍️ Start a Discussion
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Share your travel experience, ask for advice, or help other travelers
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Destination / Country *</InputLabel>
                <Select
                  value={categoryId}
                  label="Destination / Country *"
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  {Object.entries(categoriesByRegion).map(([region, cats]) => [
                    <MenuItem key={`header-${region}`} disabled sx={{ fontWeight: 700, backgroundColor: '#f5f5f5' }}>
                      {region}
                    </MenuItem>,
                    ...cats.map(cat => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.flag_emoji} {cat.name}
                      </MenuItem>
                    ))
                  ])}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Title *"
                placeholder="e.g., 10 Days in Italy - Itinerary & Tips"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ mb: 3 }}
                inputProps={{ maxLength: 200 }}
                helperText={`${title.length}/200 characters`}
              />

              <TextField
                fullWidth
                label="Your Post *"
                placeholder="Share your experience, tips, or questions..."
                multiline
                rows={12}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                sx={{ mb: 3 }}
                helperText={`${content.length}/10000 characters (minimum 20)`}
                error={content.length > 0 && content.length < 20}
              />

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Add Tags (press Enter)"
                  placeholder="e.g., budget, food, hiking"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  helperText="Add up to 5 tags to help others find your post"
                />
                {tags.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    {tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onDelete={() => handleRemoveTag(tag)}
                        size="small"
                      />
                    ))}
                  </Stack>
                )}
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={isAnonymous}
                    onChange={(e) => setIsAnonymous(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Post anonymously
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Your name won't be shown on this post
                    </Typography>
                  </Box>
                }
                sx={{ mb: 3, ml: 0 }}
              />

              <Stack direction="row" spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  sx={{
                    backgroundColor: '#8B1538',
                    px: 4,
                    '&:hover': { backgroundColor: '#6d1029' },
                  }}
                >
                  {loading ? 'Publishing...' : 'Publish Discussion'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/forum')}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ mt: 4, p: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            💡 Tips for a Great Post
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2">• Be specific with your title - mention the destination and topic</Typography>
            <Typography variant="body2">• Include practical details like dates, budget, and duration</Typography>
            <Typography variant="body2">• Share both the highlights and challenges of your experience</Typography>
            <Typography variant="body2">• Add relevant tags to help others find your post</Typography>
            <Typography variant="body2">• Be respectful and helpful to the community</Typography>
          </Stack>
        </Box>
      </Container>
    </>
  );
};

export default ForumNewPost;

